/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Case, Default, Switch } from "react-if";
import styled from "styled-components";
import { Col, Row, Text, theme } from "@nordcloud/gnui";
import { Box, DataContainer, DataItem, Details } from "~/components";
import { useCurrency } from "~/hooks/useCurrency";
import { formatMoney } from "~/tools";
import { NOT_AVAILABLE_TEXT } from "./constants";
import { useGetSectionDetails } from "./hooks";
import { SavingsPlanProps } from "./types";

const BorderBox = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${theme.color.border.border01};
`;

type DetailsProps = {
  label: string;
  value: string;
};

type Props = {
  instanceType: string;
  savingsPlansData: SavingsPlanProps;
  isReservedInstance: boolean;
  paymentOption: string;
  billingPlan: string;
  upfront: string;
  monthly: string;
  details: DetailsProps[];
  isReservationsAzure: boolean;
  skuName: string;
  nordcloudId: string;
};

export function InstanceDetails({
  instanceType,
  savingsPlansData,
  isReservedInstance,
  paymentOption,
  billingPlan,
  upfront,
  monthly,
  details,
  isReservationsAzure,
  skuName,
  nordcloudId,
}: Props) {
  const data = useGetSectionDetails({
    instanceType,
    savingsPlansData,
    isReservedInstance,
    isReservationsAzure,
    skuName,
    nordcloudId,
  });

  const { currency } = useCurrency();

  return (
    <Box
      color={theme.color.background.ui03}
      border={theme.color.border.border01}
    >
      <BorderBox>
        <Row>
          <Col>
            <Text isTitle size="sm" tag="span" color={theme.color.text.text02}>
              {data?.sectionText}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col>
            <Text size="xl" weight="medium">
              {data?.sectionValue}
            </Text>
            <Details details={details} />
          </Col>
        </Row>
      </BorderBox>
      <Col>
        <Box
          radius="0"
          padding={`${theme.spacing.spacing04} ${theme.spacing.spacing01}`}
        >
          <DataContainer>
            <Switch>
              <Case condition={isReservedInstance}>
                <DataItem
                  label="Payment Option"
                  value={paymentOption}
                  size="md"
                />
                <DataItem label="Upfront" value={upfront} size="md" />
                <DataItem label="Monthly" value={monthly} size="md" />
              </Case>
              <Case condition={isReservationsAzure}>
                <DataItem label="Billing Plan" value={billingPlan} size="md" />
                <DataItem label="Upfront" value="0.00" size="md" />
                <DataItem
                  label="Monthly"
                  value={formatMoney("0", currency)}
                  size="md"
                />
              </Case>
              <Default>
                <DataItem
                  label="Payment Option"
                  value={data?.paymentValue?.toString() ?? NOT_AVAILABLE_TEXT}
                  size="md"
                />
                <DataItem
                  label="Upfront"
                  size="md"
                  value={formatMoney(
                    data?.upFrontValue ?? NOT_AVAILABLE_TEXT,
                    currency
                  )}
                />
                <DataItem
                  label="Monthly"
                  value={formatMoney(data?.monthlyCost, currency)}
                  size="md"
                />
              </Default>
            </Switch>
          </DataContainer>
        </Box>
      </Col>
    </Box>
  );
}
